import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const AFFILIATE_COOKIE_KEY = "affiliate_ref";
const AFFILIATE_COOKIE_DAYS = 30;

/**
 * Hook pour tracker les clics affiliés
 * IMPORTANT: Le premier clic est préservé pendant 30 jours
 * Les clics suivants avec d'autres codes ne remplacent PAS le cookie existant
 * Cela garantit que l'affilié original reçoit sa commission même si le client
 * revient via une relance email ou un autre lien
 */
export const useAffiliateTracking = () => {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const trackClick = async () => {
      const refCode = searchParams.get("ref");
      if (!refCode) return;

      // IMPORTANT: Vérifier si un cookie affilié valide existe déjà
      // Si oui, on ne le remplace PAS (premier clic = commission)
      const existingCode = getStoredAffiliateCode();
      if (existingCode) {
        console.log("🔒 Code affilié existant préservé:", existingCode, "- nouveau code ignoré:", refCode);
        
        // On enregistre quand même le clic pour les stats, mais le cookie reste inchangé
        try {
          const { data: affiliate } = await supabase
            .from("affiliates")
            .select("id")
            .eq("referral_code", refCode)
            .eq("is_active", true)
            .maybeSingle();

          if (affiliate) {
            await supabase.from("referral_clicks").insert({
              affiliate_id: affiliate.id,
              referral_code: refCode,
              landing_page: window.location.pathname,
              referrer_url: document.referrer || null,
              user_agent: navigator.userAgent,
            });
            console.log("📊 Clic enregistré pour stats (sans remplacement cookie)");
          }
        } catch (error) {
          console.error("Error tracking affiliate click:", error);
        }
        return;
      }

      // Premier clic: Stocker le code avec expiration 30 jours
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + AFFILIATE_COOKIE_DAYS);
      
      localStorage.setItem(AFFILIATE_COOKIE_KEY, JSON.stringify({
        code: refCode,
        expires: expirationDate.toISOString(),
        firstClickAt: new Date().toISOString(), // Date du premier clic
      }));

      console.log("✅ Premier clic affilié enregistré:", refCode, "- expire le:", expirationDate.toLocaleDateString());

      try {
        const { data: affiliate } = await supabase
          .from("affiliates")
          .select("id")
          .eq("referral_code", refCode)
          .eq("is_active", true)
          .maybeSingle();

        if (!affiliate) {
          console.log("⚠️ Code affilié invalide ou inactif:", refCode);
          return;
        }

        await supabase.from("referral_clicks").insert({
          affiliate_id: affiliate.id,
          referral_code: refCode,
          landing_page: window.location.pathname,
          referrer_url: document.referrer || null,
          user_agent: navigator.userAgent,
        });
        
        console.log("📊 Premier clic enregistré pour affilié:", affiliate.id);
      } catch (error) {
        console.error("Error tracking affiliate click:", error);
      }
    };

    trackClick();
  }, [searchParams]);
};

/**
 * Récupère le code affilié stocké (si non expiré)
 * Retourne null si pas de code ou si expiré
 */
export const getStoredAffiliateCode = (): string | null => {
  try {
    const stored = localStorage.getItem(AFFILIATE_COOKIE_KEY);
    if (!stored) return null;
    
    const parsed = JSON.parse(stored);
    const { code, expires } = parsed;
    
    if (new Date(expires) < new Date()) {
      console.log("⏰ Cookie affilié expiré, suppression");
      localStorage.removeItem(AFFILIATE_COOKIE_KEY);
      return null;
    }
    
    return code;
  } catch {
    return null;
  }
};

/**
 * Récupère les infos complètes du tracking affilié
 */
export const getAffiliateTrackingInfo = (): { code: string; expires: string; firstClickAt: string } | null => {
  try {
    const stored = localStorage.getItem(AFFILIATE_COOKIE_KEY);
    if (!stored) return null;
    
    const parsed = JSON.parse(stored);
    
    if (new Date(parsed.expires) < new Date()) {
      localStorage.removeItem(AFFILIATE_COOKIE_KEY);
      return null;
    }
    
    return parsed;
  } catch {
    return null;
  }
};

/**
 * Efface le code affilié (après achat réussi par exemple)
 */
export const clearAffiliateCode = () => {
  localStorage.removeItem(AFFILIATE_COOKIE_KEY);
  console.log("🗑️ Cookie affilié supprimé");
};
